import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Plus, Trash2, Lock } from "lucide-react";
import { sanitizeHtml } from "@/components/RichTextEditor";

const CLEAR = "__clear__";
// Fields sourced from the participant profile — never directly editable in the
// document; kept in sync via "Refresh from profile".
const PROFILE_LOCKED_KEYS = ["participant_contacts", "stakeholder_contacts",
  "legal_guardians", "decision_makers", "informal_decision_makers"];
// Profile fields a field can be linked to but not edit from the document (derived
// on the profile: primary_address follows the Addresses list). Matches
// PROFILE_READONLY_FIELDS in backend/doc_automations.py.
const PROFILE_READONLY_FIELDS = ["primary_address"];
const isProfileLocked = (f) => PROFILE_LOCKED_KEYS.includes(f.key) || PROFILE_READONLY_FIELDS.includes(f.profile_field);
const CONTENT_STYLES = "text-sm text-slate-700 leading-relaxed [&_h1]:text-lg [&_h1]:font-semibold [&_h1]:mt-2 [&_h1]:mb-1 [&_h2]:text-base [&_h2]:font-semibold [&_h2]:mt-2 [&_h2]:mb-1 [&_h3]:text-sm [&_h3]:font-semibold [&_h3]:mt-1.5 [&_h3]:mb-1 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:mb-1.5 [&_table]:w-full [&_table]:border-collapse [&_table]:my-2 [&_table]:text-xs [&_th]:border [&_th]:border-slate-300 [&_th]:bg-slate-50 [&_th]:px-2 [&_th]:py-1 [&_th]:text-left [&_td]:border [&_td]:border-slate-200 [&_td]:px-2 [&_td]:py-1";

// Conditional visibility. A node (field or section) may declare
// visible_when = { match: "all"|"any", rules: [{field, op, value}] }.
// Legacy single-rule shape { field, op, value } is still honoured.
// op: eq | ne | in (comma list) | filled | empty. Checkbox values normalise to "true"/"false".
function evalRule(r, values) {
  if (!r || !r.field) return true;
  const norm = (v) => (v === true ? "true" : v === false ? "false" : String(v ?? ""));
  const tv = norm(values?.[r.field]);
  const op = r.op || "eq";
  if (op === "filled") return tv !== "" && tv !== "false";
  if (op === "empty") return tv === "" || tv === "false";
  if (op === "in") return String(r.value || "").split(",").map((s) => s.trim()).filter(Boolean).includes(tv);
  if (op === "ne") return tv !== norm(r.value);
  return tv === norm(r.value); // eq
}

export function isVisible(cfg, values) {
  if (!cfg) return true;
  const rules = Array.isArray(cfg.rules) ? cfg.rules : (cfg.field ? [cfg] : []);
  const active = rules.filter((r) => r && r.field);
  if (active.length === 0) return true;
  return (cfg.match || "all") === "any"
    ? active.some((r) => evalRule(r, values))
    : active.every((r) => evalRule(r, values));
}

export const isFieldVisible = (f, values) => isVisible(f?.visible_when, values);
export const isSectionVisible = (s, values) => isVisible(s?.visible_when, values);


function BasicControl({ f, value, onChange, disabled, test }) {
  const tid = test || `docf-${f.key}`;
  if (f.type === "textarea")
    return <Textarea value={value || ""} disabled={disabled} onChange={(e) => onChange(e.target.value)} className="mt-1 text-sm min-h-[70px]" data-testid={tid} />;
  if (f.type === "number")
    return <Input type="number" value={value ?? ""} disabled={disabled} onChange={(e) => onChange(e.target.value)} className="mt-1 h-9 text-sm" data-testid={tid} />;
  if (f.type === "date")
    return <Input type="date" value={value || ""} disabled={disabled} onChange={(e) => onChange(e.target.value)} className="mt-1 h-9 text-sm" data-testid={tid} />;
  if (f.type === "checkbox")
    return <label className="flex items-center gap-2 text-sm text-slate-700 mt-1"><input type="checkbox" checked={!!value} disabled={disabled} onChange={(e) => onChange(e.target.checked)} className="accent-[#bf3b4f]" data-testid={tid} /> Yes</label>;
  if (f.type === "select")
    return (
      <Select value={value || ""} onValueChange={(v) => onChange(v === CLEAR ? "" : v)} disabled={disabled}>
        <SelectTrigger className="mt-1 h-9 text-sm" data-testid={tid}><SelectValue placeholder="Select…" /></SelectTrigger>
        <SelectContent>
          {!f.required && <SelectItem value={CLEAR} className="text-slate-400">—</SelectItem>}
          {(f.options || []).map((o) => <SelectItem key={o} value={o} className="text-sm">{o}</SelectItem>)}
        </SelectContent>
      </Select>
    );
  return <Input value={value || ""} disabled={disabled} onChange={(e) => onChange(e.target.value)} className="mt-1 h-9 text-sm" data-testid={tid} />;
}

function TableControl({ f, value, onChange, disabled, canRemoveRows = false, formValues }) {
  const rows = Array.isArray(value) ? value : [];
  const cols = (f.subfields || []).filter((sf) => isFieldVisible(sf, formValues));
  const setRow = (i, k, v) => onChange(rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  // A row can be removed when either the table is fully editable (!disabled) OR when
  // the caller explicitly grants canRemoveRows (profile-locked tables: fields are read-only
  // but individual rows may still be excluded from this document instance).
  const showRemove = !disabled || canRemoveRows;
  return (
    <div className="mt-1 space-y-2" data-testid={`docf-${f.key}`}>
      {rows.map((r, i) => (
        <div key={i} className="rounded-md border border-slate-200 p-2 grid sm:grid-cols-2 gap-2 relative">
          {cols.map((sf) => (
            <div key={sf.key}>
              <Label className="text-[10px] text-slate-500">{sf.label}</Label>
              <BasicControl f={sf} value={r[sf.key]} onChange={(v) => setRow(i, sf.key, v)} disabled={disabled} test={`docf-${f.key}-${i}-${sf.key}`} />
            </div>
          ))}
          {showRemove && (
            <button
              type="button"
              onClick={() => onChange(rows.filter((_, j) => j !== i))}
              title="Remove this contact from the consent form (they remain in the participant's contact records)"
              className="absolute top-1 right-1 text-slate-400 hover:text-rose-600"
              data-testid={`docf-${f.key}-remove-${i}`}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      ))}
      {!disabled && <Button type="button" size="sm" variant="outline" className="h-7 text-xs" onClick={() => onChange([...rows, {}])}><Plus className="h-3.5 w-3.5 mr-1" />Add row</Button>}
    </div>
  );
}

// Preview-only styling for the AI builder's before/after view.
const MARK_STYLES = {
  added: "ring-2 ring-emerald-400 bg-emerald-50/60",
  changed: "ring-2 ring-amber-400 bg-amber-50/60",
  moved: "ring-2 ring-sky-400 bg-sky-50/60",
  removed: "ring-2 ring-rose-400 bg-rose-50/60 opacity-80",
};
const MARK_LABEL = { added: "New", changed: "Changed", moved: "Moved", removed: "Removed" };
const MARK_BADGE = {
  added: "bg-emerald-100 text-emerald-700", changed: "bg-amber-100 text-amber-700",
  moved: "bg-sky-100 text-sky-700", removed: "bg-rose-100 text-rose-700",
};
const MarkBadge = ({ mark }) => mark ? <span className={`ml-2 rounded px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide ${MARK_BADGE[mark]}`}>{MARK_LABEL[mark]}</span> : null;

// marks = { fields: {id: mark}, sections: {id: mark} } and showAll are used by the AI builder
// preview only: marks highlight what the AI changed; showAll ignores conditional visibility.
export function DocumentForm({ schema, values, onChange, disabled = false, marks = null, showAll = false }) {
  const setVal = (k, v) => onChange({ ...values, [k]: v });
  const fMark = (f) => marks?.fields?.[f.id];
  const sMark = (s) => marks?.sections?.[s.id];
  return (
    <div className="space-y-5">
      {(schema || []).filter((s) => showAll || isSectionVisible(s, values)).map((s) => (
        <div key={s.id} className={`rounded-lg border border-slate-200 bg-white p-4 ${MARK_STYLES[sMark(s)] || ""}`} data-testid={`docsection-${s.id}`}>
          <div className="text-sm font-semibold text-slate-800">{s.title}<MarkBadge mark={sMark(s)} />
            {showAll && s.visible_when && <span className="ml-2 rounded bg-slate-100 px-1.5 py-0.5 text-[9px] font-medium text-slate-500">Conditional</span>}
          </div>
          {s.description && <p className="text-[11px] text-slate-500 mt-0.5 mb-2">{s.description}</p>}
          <div className="grid sm:grid-cols-2 gap-3 mt-2">
            {(s.fields || []).filter((f) => showAll || isFieldVisible(f, values)).map((f) => (
              <div key={f.id} className={`${f.width === "full" || f.type === "textarea" || f.type === "table" || f.type === "image" || f.type === "content" ? "sm:col-span-2" : ""} ${MARK_STYLES[fMark(f)] ? `${MARK_STYLES[fMark(f)]} rounded-md p-2 -m-1` : ""}`}>
                {(fMark(f) || (showAll && f.visible_when)) && (
                  <div className="mb-0.5"><MarkBadge mark={fMark(f)} />
                    {showAll && f.visible_when && <span className="ml-1 rounded bg-slate-100 px-1.5 py-0.5 text-[9px] font-medium text-slate-500">Conditional</span>}</div>
                )}
                {f.type === "content" ? (
                  <>
                    {f.label && <Label className="text-[11px] font-semibold text-slate-600">{f.label}</Label>}
                    <div className={CONTENT_STYLES} data-testid={`docf-content-${f.key || f.id}`} dangerouslySetInnerHTML={{ __html: sanitizeHtml(f.content || "") }} />
                  </>
                ) : f.type === "image" ? (
                  <>
                    {f.label && <Label className="text-[11px] font-semibold text-slate-600">{f.label}</Label>}
                    {f.image_url ? <img src={f.image_url} alt={f.label || "image"} className="mt-1 max-w-full rounded border border-slate-200" data-testid={`docf-img-${f.key}`} /> : <div className="text-[11px] text-slate-400">No image set.</div>}
                  </>
                ) : (
                  <>
                    <Label className="text-[11px] font-semibold text-slate-600">{f.label}{f.required && <span className="text-rose-500"> *</span>}
                      {isProfileLocked(f) && <span className="ml-2 inline-flex items-center gap-1 text-[9px] font-medium uppercase tracking-wide text-slate-400"><Lock className="h-2.5 w-2.5" />From profile</span>}
                      {f.profile_field && !isProfileLocked(f) && <span className="ml-2 inline-flex items-center gap-1 text-[9px] font-medium uppercase tracking-wide text-emerald-500">↕ Profile</span>}
                    </Label>
                    {f.help && <p className="text-[10px] text-slate-400">{f.help}</p>}
                    {f.type === "table"
                      ? <TableControl f={f} value={values?.[f.key]} onChange={(v) => setVal(f.key, v)} disabled={disabled || PROFILE_LOCKED_KEYS.includes(f.key)} canRemoveRows={!disabled && PROFILE_LOCKED_KEYS.includes(f.key)} formValues={values} />
                      : <BasicControl f={f} value={values?.[f.key]} onChange={(v) => setVal(f.key, v)} disabled={disabled || isProfileLocked(f)} />}
                  </>
                )}
              </div>
            ))}
            {(s.fields || []).length === 0 && <div className="text-xs text-slate-400 sm:col-span-2">No fields in this section.</div>}
          </div>
        </div>
      ))}
      {(schema || []).length === 0 && <div className="text-sm text-slate-400">This document has no sections yet.</div>}
    </div>
  );
}
